import { Component } from '@theme/component';
import { debounce, isClickedOutside, onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
/**
 * The body scroll lock is one piece of shared state, but dialogs stack: the quick-add
 * modal opens the Notify Me drawer on top of itself. Each dialog used to take and release
 * it on its own, so the inner one's close cleared `body.position` and scrolled to the
 * offset it had captured while the page was already pinned at 0 - dropping the outer,
 * still-open dialog to the top of the page. Count the holders instead: the first one in
 * records where the page really was, the last one out puts it back.
 *
 * @type {number}
 */
let scrollLockCount = 0;

/** @type {number} */
let lockedScrollY = 0;

/**
 * @param {number} scrollY - Where the page sat before the first dialog pinned it.
 */
export function lockBodyScroll(scrollY) {
  if (scrollLockCount++ > 0) return;

  lockedScrollY = scrollY;
  document.body.style.width = '100%';
  document.body.style.position = 'fixed';
  document.body.style.top = `-${scrollY}px`;
}

export function unlockBodyScroll() {
  if (scrollLockCount === 0 || --scrollLockCount > 0) return;

  document.body.style.width = '';
  document.body.style.position = '';
  document.body.style.top = '';
  window.scrollTo({ top: lockedScrollY, behavior: 'instant' });
}

export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  connectedCallback() {
    super.connectedCallback();

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  /** The scheduled open frame, while one is pending. @type {number} */
  #pendingOpenFrame = 0;

  /**
   * Shows the dialog.
   */
  showDialog() {
    const { dialog } = this.refs;

    // The open is a frame away, so `dialog.open` alone would let a second call through and
    // take the scroll lock twice for one dialog.
    if (dialog.open || this.#pendingOpenFrame) return;

    // Read before the lock pins the body, which would report 0 from here on.
    const scrollY = window.scrollY;

    // Prevent layout thrashing by separating DOM reads from DOM writes
    this.#pendingOpenFrame = requestAnimationFrame(() => {
      this.#pendingOpenFrame = 0;
      lockBodyScroll(scrollY);

      dialog.showModal();
      this.dispatchEvent(new DialogOpenEvent());

      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);
    });
  }

  /**
   * Closes the dialog.
   */
  closeDialog = async () => {
    const { dialog } = this.refs;

    // Closed before the open frame ran: drop the frame instead of letting it open a dialog
    // nobody wants any more. Previously this hit the `!dialog.open` bail below, did
    // nothing, and the dialog opened anyway a frame later - now also leaving the scroll
    // lock held by a dialog that was never closed. rAF is throttled in a background tab,
    // so this window is not always one frame wide.
    if (this.#pendingOpenFrame) {
      cancelAnimationFrame(this.#pendingOpenFrame);
      this.#pendingOpenFrame = 0;
      return;
    }

    if (!dialog.open) return;

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    // Force browser to restart animation by resetting it
    // Temporarily remove any existing animation state
    dialog.style.animation = 'none';

    // Force a reflow
    void dialog.offsetWidth;

    // Now add the closing class and restore animation
    dialog.classList.add('dialog-closing');
    dialog.style.animation = '';

    await onAnimationEnd(dialog, undefined, {
      subtree: false,
    });

    unlockBodyScroll();

    dialog.close();
    dialog.classList.remove('dialog-closing');

    this.dispatchEvent(new DialogCloseEvent());
  };

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick(event) {
    const { dialog } = this.refs;

    // A modal's backdrop click targets the dialog element itself, so a nested dialog's
    // backdrop reaches us as a click on *that* dialog - and isClickedOutside falls back to
    // a coordinate test, which reads it as a click beside this one. Leave it to the dialog
    // the shopper actually clicked in.
    if (event.target instanceof HTMLDialogElement && event.target !== dialog) return;

    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.closeDialog();
  }

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute('scroll-lock')) {
        const { open } = event.target;
        if (open) {
          document.documentElement.setAttribute('scroll-lock', '');
        } else {
          document.documentElement.removeAttribute('scroll-lock');
        }
      }
    }
  },
  { capture: true }
);
